import React, { useCallback, useContext, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRotation } from './shared/useRotation';
import { AlertTypes, Button, Drawer, Icon, useDrawer, ActionModal } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { getListApplicationRegistrationsGroupedQueryKey } from '@/generated/application-registrations';
import {
	useDeleteApplicationRegistration,
	useUpdateApplicationRegistration,
} from '@/generated/application-registrations';
import {
	AccountId,
	RegistrationCompositeDetailsResponse,
	ApplicationRegistrationRequest,
	RegistrationCompositeId,
} from '@/generated/data.schemas';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './ApplicationDetail.module.scss';
import { ApplicationForm } from './shared/ApplicationForm';

interface RegisterApplicationProps {
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	application?: RegistrationCompositeDetailsResponse;
}

export const EditApplication: React.FunctionComponent<RegisterApplicationProps> = ({
	accountId,
	registrationCompositeId,
	application,
}) => {
	const formId = 'edit-application-form'; // used for binding form and submit button
	const { segmentationId } = useContext(SegmentationContext);

	const { data: translations } = useGetTranslations(segmentationId);
	const addNotification = useNotificationStore((state) => state.addNotification);

	const queryClient = useQueryClient();

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
	const [hasFormChanged, setHasFormChanged] = useState(false);

	const ariaControlId = 'edit-application';
	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const innerBody = useRef<HTMLDivElement>(null);

	const { desktopWidth } = useRotation(innerBody?.current, isOpen);

	const updateApplicationRegistrationMutation = useUpdateApplicationRegistration({
		mutation: {
			onSuccess: async () => {
				// refresh application registration grouped list query cache
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});

				// refresh application list query cache
				if (application?.id) {
					await queryClient.invalidateQueries({
						queryKey: getGetRegistrationCompositeQueryKey(
							segmentationId,
							accountId,
							registrationCompositeId,
						),
					});
				}

				addNotification({
					children: translations?.data.applicationRegistrations.saveSuccess,
					type: AlertTypes.SUCCESS,
				});

				handleClose();
			},
			onError: (axiosError) => {
				const error = axiosError.response?.data;
				if (!error) {
					console.error('Unknown error', axiosError);
				} else if (isHttpValidationProblemDetails(error)) {
					addNotification({
						children: translations?.data.applicationRegistrations.saveFailed,
						type: AlertTypes.DANGER,
					});

					setValidationErrors(error.errors);
				} else {
					addNotification({
						children: translations?.data.shared.genericErrorMsg,
						type: AlertTypes.DANGER,
					});
				}
			},
		},
	});

	const deleteApplicationRegistrationMutation = useDeleteApplicationRegistration({
		mutation: {
			onSuccess: async () => {
				// refresh application registration grouped list query cache
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				closeModal();
				handleClose();

				addNotification({
					children: translations?.data.applications.deleteApplicationRegistrations.success,
					type: AlertTypes.SUCCESS,
				});
			},
			onError: () => {
				addNotification({
					children: translations?.data.applications.deleteApplicationRegistrations.failed,
					type: AlertTypes.DANGER,
				});
			},
		},
	});

	const openDrawer = useCallback((): void => {
		setHasFormChanged(false);
		handleOpen();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// set has input text changed state - used for disabling the save button
	const handleChange = (): void => {
		if (hasFormChanged) return;
		setHasFormChanged(true);
	};

	const openModal = useCallback(() => {
		setIsDeleteConfirmOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsDeleteConfirmOpen(false);
	}, []);

	const handleRemoveApplication = () => {
		if (!application) return;

		deleteApplicationRegistrationMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			applicationId: application.id ?? 0,
		});
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		const formData = new FormData(event.target);

		setValidationErrors(null);
		const applicationRegistrationForm: ApplicationRegistrationRequest = {
			name: String(formData.get('name')) || '',
			segmentSystemName: String(formData.get('segment')) || '',
			applicationSystemName: String(formData.get('application')) || '',
			brand: String(formData.get('brand')),
			model: String(formData.get('model')),
			fluidType: String(formData.get('fluidType')),
			fluidBrand: String(formData.get('fluidBrand')),
			oilSystem: String(formData.get('oilSystem')),
			oilSystemBrand: String(formData.get('oilSystemBrand')),
			powerSupply: String(formData.get('powerSupply')),
			viscosity: formData.get('viscosity') === '' ? null : Number(formData.get('viscosity')),
			systemVolume: formData.get('systemVolume') === '' ? null : Number(formData.get('systemVolume')),
			minAmbientTemperature:
				formData.get('minAmbientTemperature') === '' ? null : Number(formData.get('minAmbientTemperature')),
			maxAmbientTemperature:
				formData.get('maxAmbientTemperature') === '' ? null : Number(formData.get('maxAmbientTemperature')),
			minOperationTemperature:
				formData.get('minOperationTemperature') === '' ? null : Number(formData.get('minOperationTemperature')),
			maxOperationTemperature:
				formData.get('maxOperationTemperature') === '' ? null : Number(formData.get('maxOperationTemperature')),
			normalOperationTemperature:
				formData.get('normalOperationTemperature') === ''
					? null
					: Number(formData.get('normalOperationTemperature')),
			additionalInformation: String(formData.get('additionalInformation')),
		};

		updateApplicationRegistrationMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			applicationId: application?.id ?? 0,
			data: applicationRegistrationForm,
		});
	};

	return (
		<>
			{/* Trigger button */}
			<Button
				aria-label={translations?.data.applications.editApplication}
				variant={'ghost'}
				onClick={openDrawer}
				aria-expanded={isOpen}
				aria-controls={ariaControlId}
			>
				<Icon name={'edit'} size={'sm'} />
			</Button>

			{isRendered && (
				<Drawer
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
					desktopWidth={`${desktopWidth}px`}
					direction={'Right'}
				>
					<Drawer.Header title={translations?.data.applications.editApplication} handleClose={handleClose} />

					<Drawer.Body>
						<div className={styles.innerBody} ref={innerBody}>
							<ApplicationForm
								formId={formId}
								legend={translations?.data.applications.editApplication}
								application={application}
								isOpen={isOpen}
								validationErrors={validationErrors}
								handleSubmit={handleSubmit}
								handleChange={handleChange}
								isFullHeight={desktopWidth === 720}
							/>

							<Button onClick={openModal} variant={'outline'} isFullWidth className={styles.marginTop}>
								{translations?.data.shared.delete}
								<Icon name={'trash'} size={'md'} />
							</Button>

							{isDeleteConfirmOpen && (
								<ActionModal
									size={'sm'}
									translations={translations?.data}
									onSubmit={handleRemoveApplication}
									onCancel={closeModal}
									headline={translations?.data.applications.deleteApplicationRegistrations.headline}
									message={translations?.data.applications.deleteApplicationRegistrations.teaser}
								/>
							)}
						</div>
					</Drawer.Body>

					<Drawer.Footer className={styles.footer} hasNoPadding>
						<Button variant={'ghost'} isFullWidth onClick={handleClose}>
							{translations?.data.shared.cancel}
						</Button>
						<Button type="submit" form={formId} variant={'primary'} isFullWidth disabled={!hasFormChanged}>
							{translations?.data.applicationRegistrations.save}
						</Button>
					</Drawer.Footer>
				</Drawer>
			)}
		</>
	);
};
